/**
 * useMutation hooks for the Publication View write path — section + publication
 * CRUD. Replaces the mutation effects in `publicationViewEffects`
 * (compose-app).
 *
 * Auth (§1): GraphQL writes go through `postQuery` (cookie/session auth) and
 * REST writes through `transport` — NOT bnp-core Auth0 helpers. Publication
 * View is reachable by non-admins.
 *
 * Cache: each hook invalidates the broad query-key prefix(es) it affects
 * (`foldersHierarchy`, `publication`, `stories`) on success, so every param
 * variant in cache refetches — replacing the effects' explicit
 * `getPublicationFoldersHierarchy` / `getPublicationById` re-dispatches.
 *
 * Orchestration left to the caller, matching the retired effects:
 *   - navigation (`navigateToPublicationView` / `...FolderList` / `...List`)
 *   - chained `updatePublicationUpdatedAt` after section/publication writes
 *   - local selection state (`setSelectedSectionId` / `...Name`)
 *   - title-aware move toasts (caller has the folder structure)
 *   - the conditional `deleteRootSection` / storyList-folder refetch in move
 *
 * Toasts use `useToast` (§8); copy is passed in by the caller.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { transport } from '@opengov/app-core/transport';
import { DATE_FORMAT_WITH_TIMEZONE, UNIQUENESS_VIOLATION } from '@opengov/stories-shared/constants';
import {
  deleteRootSectionInPublicationFolder,
  deleteStoriesAndFolderByFolderIds,
  insertPublicationFolder,
  renamePublicationSectionById,
  setTocAsLandingPage,
  updatePublicationById,
  updatePublicationUpdatedAt
} from '@opengov/stories-shared/graphQL';
import { IPublication } from '@opengov/stories-shared/types';
import moment from 'moment';
import * as qs from 'query-string';

import { postQuery } from '../../../../effects/publications/postQuery';
import {
  moveFolders,
  organizeStoriesInFolder,
  organizeStoryFolders,
  updatePublication
} from '../../../../graphQL/queries';
import { IOrganizeStoriesGraphQlRequest } from '../../../../../types';
import { getUserById } from '../../../../routes/hasura';
import { useToast } from '../../../Toast/context/useToast.context';
import { publicationViewQueryKeys } from './publicationViewQueryKeys';

export type ToastCopy = {
  title: string;
  body?: string;
};

const HIERARCHY_KEY = [...publicationViewQueryKeys.all, 'foldersHierarchy'];
const PUBLICATION_KEY = [...publicationViewQueryKeys.all, 'publication'];

// ---------------------------------------------------------------------------
// createSection — REST POST .../section/create
// ---------------------------------------------------------------------------

export type CreateSectionInput = {
  publicationId: string;
  entityId: number;
  userId: number;
  folderTitle: string;
  parentFolderId?: string;
  isSubSection?: boolean;
};

export type CreateSectionResult = { sectionId: string; sectionTitle: string };

export function useCreateSection(errorToast: ToastCopy, duplicateMessage: string) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<CreateSectionResult, Error, CreateSectionInput>({
    mutationFn: async (input) => {
      const { userId, folderTitle, entityId, parentFolderId, publicationId, isSubSection } = input;
      const authorId = await getUserById(userId);
      const response = await transport
        .url(`/api/stories/v1/publications/${publicationId}/section/create`)
        .json({
          storyFolder: { parentFolderId, title: folderTitle, entityId, publicationId },
          publicationId,
          createdBy: authorId,
          isSubSection
        })
        .post()
        .json();
      if (!response.storyFolder) {
        return Promise.reject(response.errors);
      }
      const created = response.storyFolder.insert_storyFolders_one;
      return { sectionId: created.id, sectionTitle: created.title };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HIERARCHY_KEY });
    },
    onError: (err) => {
      const message = err?.message ?? '';
      toggleToast({
        title: errorToast.title,
        body: message.includes(UNIQUENESS_VIOLATION) ? duplicateMessage : errorToast.body,
        severity: 'error'
      });
    }
  });
}

// ---------------------------------------------------------------------------
// updatePublication — GraphQL updatePublicationById (title/description)
// ---------------------------------------------------------------------------

export type UpdatePublicationInput = {
  publicationId: string;
  entityId: number;
  title: string;
  description: string;
};

export function useUpdatePublication(errorToast: ToastCopy, duplicateMessage: string) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<IPublication, Error, UpdatePublicationInput>({
    mutationFn: async ({ publicationId, entityId, title, description }) => {
      const response = await postQuery(updatePublicationById, { publicationId, entityId, title, description });
      if (!response?.update_publications?.affected_rows) {
        return Promise.reject(response.errors);
      }
      return response.update_publications.returning[0] as IPublication;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PUBLICATION_KEY });
    },
    onError: (err: any) => {
      const message = err && err[0] ? err[0].message : err?.message ?? '';
      toggleToast({
        title: errorToast.title,
        body: message.includes(UNIQUENESS_VIOLATION) ? duplicateMessage : errorToast.body,
        severity: 'error'
      });
    }
  });
}

// ---------------------------------------------------------------------------
// updatePublicationSection — GraphQL rename
// ---------------------------------------------------------------------------

export type UpdatePublicationSectionInput = {
  entityId: number;
  title: string;
  selectedSectionId: string;
};

export type UpdatePublicationSectionResult = { title: string };

export function useUpdatePublicationSection(errorToast: ToastCopy, duplicateMessage: string) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<UpdatePublicationSectionResult, Error, UpdatePublicationSectionInput>({
    mutationFn: async ({ entityId, title, selectedSectionId }) => {
      const response = await postQuery(renamePublicationSectionById, { entityId, title, selectedSectionId });
      if (!response?.update_storyFolders?.affected_rows) {
        return Promise.reject(response.errors);
      }
      return { title: response.update_storyFolders.returning[0]?.title };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HIERARCHY_KEY });
    },
    onError: (err: any) => {
      const message = err && err[0] ? err[0].message : err?.message ?? '';
      toggleToast({
        title: errorToast.title,
        body: message.includes(UNIQUENESS_VIOLATION) ? duplicateMessage : errorToast.body,
        severity: 'error'
      });
    }
  });
}

// ---------------------------------------------------------------------------
// setTocAsLandingPage — GraphQL
// ---------------------------------------------------------------------------

export function useSetTocAsLandingPage(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<IPublication, Error, { publicationId: string; setTocAsLandingPage: boolean }>({
    mutationFn: async ({ publicationId, setTocAsLandingPage: value }) => {
      const response = await postQuery(setTocAsLandingPage, { publicationId, setTocAsLandingPage: value });
      return response.update_publications_by_pk as IPublication;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PUBLICATION_KEY });
    },
    onError: () => toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' })
  });
}

// ---------------------------------------------------------------------------
// addRootSectionInPublicationFolder — GraphQL
// ---------------------------------------------------------------------------

export function useAddRootSectionInPublicationFolder() {
  return useMutation<unknown, Error, { publicationId: string; userId: number; sectionId: string }>({
    mutationFn: async ({ publicationId, sectionId, userId }) => {
      const authorId = await getUserById(userId);
      return postQuery(insertPublicationFolder, {
        publicationFolder: { publicationId, createdBy: authorId, folderId: sectionId }
      });
    }
  });
}

// ---------------------------------------------------------------------------
// deleteSectionById — GraphQL (+ soft-rename + thumbnail refresh)
// ---------------------------------------------------------------------------

export function useDeleteSectionById(successToast: ToastCopy, errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<unknown, Error, { publicationId: string; entityId: number; sectionIds: string[] }>({
    mutationFn: async ({ sectionIds, entityId }) => {
      const response = await postQuery(deleteStoriesAndFolderByFolderIds, { folderIds: sectionIds });
      // Soft-rename the deleted root section so its name frees up for reuse,
      // matching the retired effect.
      void postQuery(renamePublicationSectionById, {
        entityId,
        title: `deleted-${Date.now()}`,
        selectedSectionId: sectionIds[0]
      });
      return response;
    },
    onSuccess: () => {
      toggleToast({ title: successToast.title, body: successToast.body, severity: 'success' });
      void queryClient.invalidateQueries({ queryKey: HIERARCHY_KEY });
    },
    onError: () => toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' })
  });
}

// ---------------------------------------------------------------------------
// organizeStorySectionsById — GraphQL (reorder)
// ---------------------------------------------------------------------------

export function useOrganizeStorySectionsById(successToast: ToastCopy, errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<unknown, Error, { orderedSectionsIds: string[]; entityId: number; publicationId: string }>({
    mutationFn: ({ orderedSectionsIds }) => postQuery(organizeStoryFolders(orderedSectionsIds), {}),
    onSuccess: () => {
      toggleToast({ title: successToast.title, body: successToast.body, severity: 'success' });
      void queryClient.invalidateQueries({ queryKey: HIERARCHY_KEY });
    },
    onError: () => toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' })
  });
}

// ---------------------------------------------------------------------------
// organizeStoriesById — GraphQL (reorder stories within a section/folder).
// Replaces the retired `storyListEffects.organizeStoriesById`. Success/error
// toasts via the hook; caller refetches its own view (getPublicationStories).
// ---------------------------------------------------------------------------

export function useOrganizeStoriesInFolder(successToast: ToastCopy, errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<unknown, Error, IOrganizeStoriesGraphQlRequest>({
    mutationFn: ({ orderedStoriesIds }) => postQuery(organizeStoriesInFolder(orderedStoriesIds), {}),
    onSuccess: () => {
      toggleToast({ title: successToast.title, body: successToast.body, severity: 'success' });
      void queryClient.invalidateQueries({ queryKey: HIERARCHY_KEY });
    },
    onError: () => toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' })
  });
}

// ---------------------------------------------------------------------------
// moveSection — GraphQL moveFolders. Caller handles the conditional
// deleteRootSection / storyList refetch + title-aware toast (it has the
// folder structure), matching the retired effect's branches.
// ---------------------------------------------------------------------------

export function useMoveSection() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { ids: string[]; parentFolderId: string }>({
    mutationFn: ({ ids, parentFolderId }) => postQuery(moveFolders, { ids, parentFolderId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HIERARCHY_KEY });
    }
  });
}

// ---------------------------------------------------------------------------
// deleteRootSectionInPublicationFolder — GraphQL
// ---------------------------------------------------------------------------

export function useDeleteRootSectionInPublicationFolder() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { publicationId: string; sectionId: string }>({
    mutationFn: ({ publicationId, sectionId }) =>
      postQuery(deleteRootSectionInPublicationFolder, { publicationId, sectionId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HIERARCHY_KEY });
    }
  });
}

// ---------------------------------------------------------------------------
// updatePublicationUpdatedAt — GraphQL (refreshes publication.updatedAt)
// ---------------------------------------------------------------------------

export function useUpdatePublicationUpdatedAt() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { publicationId: string; userId: number }>({
    mutationFn: async ({ publicationId, userId }) => {
      const authorId = await getUserById(userId);
      const formattedDate = moment(new Date()).format(DATE_FORMAT_WITH_TIMEZONE);
      return postQuery(updatePublicationUpdatedAt, { publicationId, updatedAt: formattedDate, userId: authorId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PUBLICATION_KEY });
    }
  });
}

// ---------------------------------------------------------------------------
// updatePublicationThumbnail — GraphQL updatePublication(thumbnailPath)
// ---------------------------------------------------------------------------

export function useUpdatePublicationThumbnail() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { publicationId: string; thumbnailPath: string }>({
    mutationFn: ({ publicationId, thumbnailPath }) =>
      postQuery(updatePublication, { id: publicationId, data: { thumbnailPath } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PUBLICATION_KEY });
    }
  });
}

// ---------------------------------------------------------------------------
// setPublicationThumbnail — REST POST .../updateThumbnail
// ---------------------------------------------------------------------------

export function useSetPublicationThumbnail() {
  return useMutation<unknown, Error, { publicationId: string; entityId: number; entitySubdomain: string }>({
    mutationFn: async ({ publicationId, entityId, entitySubdomain }) => {
      const response = await transport
        .url(`/api/stories/v1/publications/${publicationId}/updateThumbnail`)
        .json({ entityId, entitySubdomain, publicationId })
        .post()
        .json();
      return response;
    }
  });
}

// ---------------------------------------------------------------------------
// clonePublication — REST POST .../copy → copyPublicationJobId (poll in caller)
// ---------------------------------------------------------------------------

export type ClonePublicationInput = {
  sourcePublicationId: string;
  destinationPublicationId: string;
  copyType: string;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
};

export function useClonePublication(errorToast: ToastCopy) {
  const { toggleToast } = useToast();

  return useMutation<string, Error, ClonePublicationInput>({
    mutationFn: async (input) => {
      const { sourcePublicationId, destinationPublicationId, copyType, entityId, entityUuid, entitySubdomain } =
        input;
      const data = await transport
        .url(`/api/stories/v1/publications/${sourcePublicationId}/copy`)
        .json({
          entity_id: entityId,
          entity_uuid: entityUuid,
          entity_subdomain: entitySubdomain,
          destination_publication_id: destinationPublicationId,
          copy_type: copyType
        })
        .post()
        .json();
      return data.copyPublicationJobId as string;
    },
    onError: () => toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' })
  });
}

// ---------------------------------------------------------------------------
// exportPublicationPdf — REST POST .../export-pdf → process status (poll caller)
// ---------------------------------------------------------------------------

export function useExportPublicationPdf(errorToast: ToastCopy) {
  const { toggleToast } = useToast();

  return useMutation<unknown, Error, { publicationId: string; entityId: number; pdfOptions: Record<string, unknown> }>({
    mutationFn: async ({ publicationId, entityId, pdfOptions }) => {
      const response = await transport
        .url(`/api/stories/v1/publication/${publicationId}/export-pdf`)
        .json({ pdf_options: { ...pdfOptions, cover_story_id: null }, entity_id: entityId })
        .post()
        .json();
      return response;
    },
    onError: () => toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' })
  });
}

// ---------------------------------------------------------------------------
// previewPublicationPdf — REST GET .../preview-pdf → pdf link. On-demand,
// abortable via a caller-supplied AbortController (rapid form changes).
// Returns '' on abort; real failures reject so the mutation reports isError,
// matching the retired effect (which only surfaced non-abort errors).
// ---------------------------------------------------------------------------

export type PreviewPublicationPdfInput = {
  publicationId: string;
  entityId: number;
  entitySubdomain: string;
  pdfOptions: { margins: unknown; [key: string]: unknown };
  controller?: AbortController;
};

export function usePreviewPublicationPdf() {
  return useMutation<string, Error, PreviewPublicationPdfInput>({
    mutationFn: async ({ publicationId, entityId, entitySubdomain, pdfOptions, controller }) => {
      const options = {
        ...pdfOptions,
        entityId,
        entitySubdomain,
        margins: encodeURIComponent(JSON.stringify(pdfOptions.margins))
      };
      const query = qs.stringify(options, { encode: false });
      let req = transport.url(`/api/stories/v1/publication/${publicationId}/preview-pdf?${query}`);
      if (controller) req = req.signal(controller);
      const response = await req
        .get()
        .json()
        .catch((error: { name?: string }) => {
          // Aborts are expected (a newer preview is already in flight) — resolve
          // to '' so `mutateAsync` never rejects at the call site. Real failures
          // propagate so the mutation enters its error state and the modal can
          // show the preview-failure message.
          if (error?.name === 'AbortError') return { pdf: '' };
          throw error;
        });
      return response.pdf;
    }
  });
}

// ---------------------------------------------------------------------------
// bulkStatusChange — REST POST .../bulk-change-status → jobId (poll caller)
// ---------------------------------------------------------------------------

export type BulkStatusChangeInput = {
  publicationId: string;
  entityId: number;
  entityUuid: string;
  selectedStoriesIds: string[];
  selectChangeStatus: string;
  selectAllStories: boolean;
  sectionId: string;
  searchText: string;
  storyState: string;
  show: string;
  isFollowable: boolean;
};

export function useBulkStatusChange(errorToast?: ToastCopy) {
  const { toggleToast } = useToast();

  return useMutation<string, Error, BulkStatusChangeInput>({
    mutationFn: async (input) => {
      const data = await transport
        .url(`/api/stories/v1/publications/${input.publicationId}/bulk-change-status`)
        .json({
          entity_id: input.entityId,
          entity_uuid: input.entityUuid,
          selected_stories_ids: input.selectedStoriesIds,
          select_change_status: input.selectChangeStatus,
          select_all_stories: input.selectAllStories,
          section_id: input.sectionId,
          search_text: input.searchText,
          story_state: input.storyState,
          show: input.show,
          is_followable: input.isFollowable
        })
        .post()
        .json();
      return data.bulkStatusChangeJobId as string;
    },
    onError: () => {
      if (!errorToast) return;
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}
